/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import {
  attachRequestIdToConfig,
  getRequestIdFromHeaders,
} from "../utils/requestCorrelation";
import {
  finalizeAuthenticatedApiError,
  getErrorMessageFromPayload,
  parseErrorPayload,
} from "./errorResponseHelpers";

/**
 * Builds a normalized error for fetch failures that never reached a response.
 *
 * @param {unknown} cause - The thrown fetch error.
 * @param {string|null} requestId - The request id attached to the call.
 * @param {string} defaultErrorMessage - Fallback message shown to the user.
 * @returns {Error} The normalized fetch error.
 */
const buildNetworkError = (cause, requestId, defaultErrorMessage) => {
  const error = new Error(defaultErrorMessage);

  error.name = "AuthenticatedFetchError";
  error.requestId = requestId;
  error.cause = cause;

  return error;
};

/**
 * Builds a rejection error for a non-OK fetch response.
 *
 * @param {Response|object} response - The failed response object.
 * @param {string|null} requestId - The request id attached to the call.
 * @param {string} defaultErrorMessage - Fallback message if the body is empty.
 * @returns {Promise<Error>} The enriched fetch error.
 */
const buildFetchError = async (response, requestId, defaultErrorMessage) => {
  const payload = await parseErrorPayload(response);
  const error = new Error(
    getErrorMessageFromPayload(payload, defaultErrorMessage),
  );

  error.name = "AuthenticatedFetchError";
  error.requestId = requestId;
  error.response = {
    status: response?.status,
    statusText: response?.statusText,
    data: payload,
    headers: response?.headers,
    requestId,
  };

  return error;
};

/**
 * Executes a cookie-authenticated fetch request with request-id correlation and
 * normalized error handling for download/export flows.
 *
 * @param {RequestInfo|URL|string} input - The request URL or input object.
 * @param {object} [init={}] - Fetch init options plus optional wrapper settings.
 * @param {RequestCredentials} [init.credentials="include"] - Credentials mode for the request.
 * @param {string} [init.defaultErrorMessage="Request failed"] - Fallback user-facing message.
 * @returns {Promise<{response: Response}>} The raw response.
 * @throws {Error} Throws an enriched error when the response is not OK.
 */
export const authenticatedFetch = async (input, init = {}) => {
  const {
    credentials = "include",
    defaultErrorMessage = "Request failed",
    ...requestInit
  } = init;
  const requestConfig = attachRequestIdToConfig({
    ...requestInit,
    credentials,
  });
  let response;

  try {
    response = await fetch(input, requestConfig);
  } catch (error) {
    throw buildNetworkError(error, requestConfig.requestId, defaultErrorMessage);
  }

  const requestId = getRequestIdFromHeaders(response?.headers) || requestConfig.requestId;

  if (response?.ok) {
    return {
      response,
    };
  }

  const error = await buildFetchError(response, requestId, defaultErrorMessage);

  throw finalizeAuthenticatedApiError(error, requestId);
};

export default authenticatedFetch;
